import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHappeningNow } from '../../api/happeningNow';
import { useAuth } from '../AuthContext.jsx';
import { goToSignup } from '../../utils/guestSearch';
import PlatformIcon from '../PlatformIcon.jsx';
import DiscoveryChrome from './DiscoveryChrome.jsx';
import {
  PLATFORM_CHIPS,
  categoriesFromItems,
  filterDiscoveryItems,
  flattenDiscoveryItems,
  itemKey,
  itemLogoUrl,
  itemThumbnail,
  itemUsesChannelMark,
  platformTone,
  platformViewerCount,
  railShowsMoreHint,
  safeItemMeta,
  scopesFromSections,
  statusOf,
  timeFiltersForItems,
  todayRailGroups,
} from './discoveryItems';
import { cssUrlValue } from '../../utils/mediaUrl';
import '../../styles/homeDiscovery.css';

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

function Badge({ status }) {
  if (status === 'live') {
    return <span className="home-disc-badge home-disc-badge--live">LIVE</span>;
  }
  if (status === 'soon') {
    return <span className="home-disc-badge home-disc-badge--soon">Starting soon</span>;
  }
  return null;
}

function DiscoveryRail({ itemCount, children }) {
  const railRef = useRef(null);
  const [showHint, setShowHint] = useState(false);

  useLayoutEffect(() => {
    const el = railRef.current;
    if (!el) return undefined;
    const measure = () => setShowHint(railShowsMoreHint(el));
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [itemCount]);

  return (
    <div className="home-disc-rail-wrap">
      <div ref={railRef} className="home-disc-rail">
        {children}
      </div>
      {showHint ? (
        <div className="home-disc-rail-more" aria-hidden="true">
          <span>scroll for more</span>
        </div>
      ) : null}
    </div>
  );
}

function EventCard({ item, onWatch, onRemind, dateStyle }) {
  const status = statusOf(item);
  const live = status === 'live';
  const meta = safeItemMeta(item, { dateStyle });
  const viewers = platformViewerCount(item);
  const cover = itemUsesChannelMark(item) ? null : itemThumbnail(item);
  const logo = itemLogoUrl(item);
  const mark = !cover;
  const letter = String(item.name || item.channel || '?').charAt(0).toUpperCase();
  return (
    <article className="home-disc-card">
      <div
        className={`home-disc-art${mark ? ' home-disc-art--mark' : ''}`}
        style={cover ? { background: platformTone(item.platform) } : undefined}
      >
        {cover ? <img className="home-disc-thumb" src={cover} alt="" /> : null}
        {mark && logo ? (
          <div className="home-disc-art-bg" style={{ backgroundImage: cssUrlValue(logo) }} aria-hidden="true" />
        ) : null}
        {mark ? (
          <div className="home-disc-art-mark">
            {logo ? <img src={logo} alt="" /> : <span aria-hidden="true">{letter}</span>}
          </div>
        ) : null}
        <Badge status={status} />
        {viewers != null ? (
          <span className="home-disc-viewers">{viewers.toLocaleString()} watching</span>
        ) : null}
        <span className="home-disc-plat">
          <PlatformIcon platform={item.platform} />
        </span>
      </div>
      <div className="home-disc-body">
        <h3>{item.name}</h3>
        <p>{meta || '\u00a0'}</p>
        <div className="home-disc-actions">
          {live && item.url ? (
            <button type="button" className="act act-watch" onClick={() => onWatch(item)}>
              <PlayIcon />
              Watch
            </button>
          ) : (
            <button type="button" className="act act-notify" onClick={() => onRemind(item)}>
              <BellIcon />
              Remind me
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

const HomeDiscovery = ({ onSearch, trending = [] }) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [sections, setSections] = useState([]);
  const [time, setTime] = useState('live');
  const [scopeId, setScopeId] = useState(null);
  const [category, setCategory] = useState(null);

  useEffect(() => {
    let active = true;
    getHappeningNow()
      .then((next) => {
        if (!active) return;
        setSections(next || []);
      })
      .catch(() => {
        if (active) setSections([]);
      });
    return () => { active = false; };
  }, []);

  const scopes = useMemo(() => scopesFromSections(sections), [sections]);
  const activeScope = scopeId || scopes.find((s) => s.scope === 'country')?.id || scopes[0]?.id || 'global';

  const allItems = useMemo(() => flattenDiscoveryItems(sections), [sections]);
  const scopedItems = useMemo(
    () => filterDiscoveryItems(allItems, { scopeId: activeScope }),
    [allItems, activeScope],
  );
  const timeFilters = useMemo(() => timeFiltersForItems(scopedItems), [scopedItems]);
  const items = useMemo(
    () => filterDiscoveryItems(allItems, { scopeId: activeScope, time, category }),
    [allItems, activeScope, time, category],
  );
  const categoryChips = useMemo(
    () => categoriesFromItems(filterDiscoveryItems(allItems, { scopeId: activeScope, time })),
    [allItems, activeScope, time],
  );

  useEffect(() => {
    if (!timeFilters.some((chip) => chip.id === time)) {
      setTime(timeFilters[0]?.id || 'live');
    }
  }, [time, timeFilters]);

  useEffect(() => {
    if (category && !categoryChips.some((chip) => chip.id === category)) {
      setCategory(null);
    }
  }, [category, categoryChips]);

  const byPlatform = useMemo(() => {
    const groups = { twitch: [], youtube: [], kick: [], epg: [] };
    for (const item of items) {
      const key = String(item.platform || '').toLowerCase();
      if (groups[key]) groups[key].push(item);
    }
    return [
      { id: 'twitch', label: 'Twitch', items: groups.twitch },
      { id: 'youtube', label: 'YouTube', items: groups.youtube },
      { id: 'kick', label: 'Kick', items: groups.kick },
      { id: 'epg', label: 'Live TV', items: groups.epg },
    ].filter((group) => group.items.length);
  }, [items]);

  const railGroups = time === 'today' ? todayRailGroups(items) : byPlatform;

  const activeScopeLabel = scopes.find((s) => s.id === activeScope)?.label || 'Worldwide';
  const timeLabel = timeFilters.find((chip) => chip.id === time)?.label || 'Live now';
  const categoryLabel = categoryChips.find((chip) => chip.id === category)?.label;
  const railTitle = [timeLabel, activeScopeLabel, categoryLabel].filter(Boolean).join(' · ');
  const shortcuts = useMemo(
    () => [...new Set((trending || []).map((item) => item.name).filter(Boolean))].slice(0, 4),
    [trending],
  );

  const onWatch = (item) => {
    if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer');
  };

  const onRemind = () => {
    if (!isLoggedIn) goToSignup(navigate);
  };

  return (
    <section className="home-disc" id="whats-on">
      <DiscoveryChrome
        time={time}
        onTime={setTime}
        timeFilters={timeFilters}
        places={scopes}
        placeId={activeScope}
        placeLabel={activeScopeLabel}
        onSelectPlace={setScopeId}
        categories={categoryChips}
        categoryId={category}
        onSelectCategory={setCategory}
        onSearch={onSearch}
        shortcuts={shortcuts}
      />

      <h3 className="home-disc-slice">{railTitle}</h3>
      {railGroups.length ? railGroups.map((group) => (
        <div key={group.id} className="home-disc-block">
          <h3 className="home-disc-block-head">
            {group.id === 'soon' || group.id === 'next' ? null : <PlatformIcon platform={group.id} />}
            {group.label}
          </h3>
          <DiscoveryRail itemCount={group.items.length}>
            {group.items.map((item, index) => (
              <EventCard
                key={itemKey(item, index)}
                item={item}
                onWatch={onWatch}
                onRemind={onRemind}
                dateStyle={time === 'week' || time === 'later' ? time : undefined}
              />
            ))}
          </DiscoveryRail>
        </div>
      )) : (
        <p className="home-disc-empty">Nothing in this slice yet. Try another time, or a search above.</p>
      )}

      <div
        className="home-disc-platforms"
        aria-label="Searching across Twitch, YouTube, Kick, and Live TV"
      >
        <span aria-hidden="true">Searching across</span>
        {PLATFORM_CHIPS.map((platform) => (
          <span key={platform.id} className="home-disc-platforms-mark" aria-hidden="true">
            <PlatformIcon platform={platform.id} />
            <span className="home-disc-platforms-name">
              <span>{platform.label}</span>
            </span>
          </span>
        ))}
      </div>
    </section>
  );
};

export default HomeDiscovery;
